import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  DollarSign,
  Save,
  Plus,
  Trash2,
  LoaderPinwheel,
  User,
  Clock,
  AlertCircle,
} from "lucide-react";
import Navbar from "../../components/ui/Navbar";

const API = process.env.REACT_APP_API_URL;

const PRIORIDAD_COLOR = {
  Baja: "bg-green-100 text-green-700",
  Media: "bg-yellow-100 text-yellow-700",
  Alta: "bg-orange-100 text-orange-700",
  Crítica: "bg-red-100 text-red-700",
};

export default function ConfigCostosPage({ embedded = false }) {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usuarios, setUsuarios] = useState([]);

  // Form general
  const [form, setForm] = useState({
    tarifaGeneralHora: 0,
    moneda: "COP",
    simboloMoneda: "$",
    redondeoMinutos: 15,
    viaticosDefault: 0,
    desplazamientosDefault: 0,
    tcoAlertaPorcentaje: 70,
  });

  // SLA config
  const [slaConfig, setSlaConfig] = useState([]);

  // Tarifa técnico form
  const [tecnicoForm, setTecnicoForm] = useState({
    usuario: "",
    tarifaHora: "",
  });
  const [addingTecnico, setAddingTecnico] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cfgRes, usersRes] = await Promise.all([
          axios.get(`${API}/costos-config`, { headers }),
          axios.get(`${API}/users`, { headers }),
        ]);
        const cfg = cfgRes.data;
        setConfig(cfg);
        setForm({
          tarifaGeneralHora: cfg.tarifaGeneralHora || 0,
          moneda: cfg.moneda || "COP",
          simboloMoneda: cfg.simboloMoneda || "$",
          redondeoMinutos: cfg.redondeoMinutos || 15,
          viaticosDefault: cfg.viaticosDefault || 0,
          desplazamientosDefault: cfg.desplazamientosDefault || 0,
          tcoAlertaPorcentaje: cfg.tcoAlertaPorcentaje || 70,
        });
        setSlaConfig(cfg.slaConfig || []);
        setUsuarios(usersRes.data || []);
      } catch (err) {
        toast.error("Error al cargar configuración");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: isNaN(Number(value))
        ? value
        : name === "moneda" || name === "simboloMoneda"
          ? value
          : Number(value),
    }));
  };

  const handleSlaChange = (prioridad, campo, valor) => {
    setSlaConfig((prev) =>
      prev.map((s) =>
        s.prioridad === prioridad ? { ...s, [campo]: Number(valor) } : s,
      ),
    );
  };

  const handleGuardar = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/costos-config`,
        { ...form, slaConfig },
        { headers },
      );
      toast.success("Configuración guardada correctamente");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleAddTecnico = async () => {
    if (!tecnicoForm.usuario || !tecnicoForm.tarifaHora) {
      return toast.error("Selecciona usuario e ingresa tarifa");
    }
    setAddingTecnico(true);
    try {
      const { data } = await axios.post(
        `${API}/costos-config/tecnicos`,
        {
          usuario: tecnicoForm.usuario,
          tarifaHora: Number(tecnicoForm.tarifaHora),
        },
        { headers },
      );
      setConfig(data.config);
      setTecnicoForm({ usuario: "", tarifaHora: "" });
      toast.success("Tarifa de técnico guardada");
    } catch (err) {
      toast.error("Error al guardar tarifa");
    } finally {
      setAddingTecnico(false);
    }
  };

  const handleDeleteTecnico = async (usuarioId) => {
    if (!window.confirm("¿Eliminar tarifa de este técnico?")) return;
    try {
      const { data } = await axios.delete(
        `${API}/costos-config/tecnicos/${usuarioId}`,
        { headers },
      );
      setConfig(data.config);
      toast.info("Tarifa eliminada");
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  if (loading) {
    return (
      <div className={embedded ? "bg-gray-50" : "min-h-screen bg-gray-50"}>
        {!embedded && <Navbar />}
        <div className="flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
          <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
          Cargando datos, por favor espera...
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "bg-gray-50" : "min-h-screen bg-gray-50"}>
      {!embedded && <Navbar />}
      <div
        className={embedded ? "w-full" : "container mx-auto px-4 py-6 pt-20 md:pt-28"}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <DollarSign className="h-7 w-7 text-green-600" /> Configuración de
              Costos
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Tarifas por hora, SLA y configuración de alertas TCO
            </p>
          </div>
          <button
            onClick={handleGuardar}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition shadow disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>

        <div className="space-y-6">
          {/* Tarifa general y moneda */}
          <Section
            icon={<DollarSign className="h-5 w-5 text-green-600" />}
            title="Tarifas generales"
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <FormField
                label="Tarifa general por hora"
                name="tarifaGeneralHora"
                type="number"
                value={form.tarifaGeneralHora}
                onChange={handleChange}
                help="Se aplica cuando el técnico no tiene tarifa individual"
              />
              <FormField
                label="Moneda"
                name="moneda"
                value={form.moneda}
                onChange={handleChange}
                help="Código ISO (COP, USD, EUR...)"
              />
              <FormField
                label="Símbolo de moneda"
                name="simboloMoneda"
                value={form.simboloMoneda}
                onChange={handleChange}
                help="Símbolo a mostrar ($, €, USD...)"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Redondeo de tiempo
                </label>
                <select
                  name="redondeoMinutos"
                  value={form.redondeoMinutos}
                  onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {[1, 5, 10, 15, 30, 60].map((v) => (
                    <option key={v} value={v}>
                      {v === 60 ? "1 hora" : `${v} minutos`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Redondear tiempo al siguiente múltiplo
                </p>
              </div>
              <FormField
                label="Viáticos por defecto"
                name="viaticosDefault"
                type="number"
                value={form.viaticosDefault}
                onChange={handleChange}
                help="Valor fijo de viáticos por servicio"
              />
              <FormField
                label="Desplazamientos por defecto"
                name="desplazamientosDefault"
                type="number"
                value={form.desplazamientosDefault}
                onChange={handleChange}
                help="Valor fijo de desplazamiento por servicio"
              />
            </div>
          </Section>

          {/* Tarifas por técnico */}
          <Section
            icon={<User className="h-5 w-5 text-blue-600" />}
            title="Tarifas por técnico"
          >
            <p className="text-xs text-gray-500 mb-4">
              Si un técnico tiene tarifa individual activa, se usará en lugar de
              la tarifa general. El valor se guarda como snapshot en cada
              registro para trazabilidad histórica.
            </p>

            {/* Lista de técnicos con tarifa */}
            {config?.tarifasTecnicos?.length > 0 ? (
              <div className="space-y-2 mb-4">
                {config.tarifasTecnicos.map((t) => (
                  <div
                    key={t._id || t.usuario?._id}
                    className="flex items-center justify-between bg-gray-50 rounded-xl p-3 border border-gray-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {t.usuario?.name || t.nombre || "Técnico"}
                        </p>
                        <p className="text-xs text-gray-400">
                          {t.usuario?.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-bold text-green-700">
                          ${Number(t.tarifaHora).toLocaleString("es-CO")}/h
                        </p>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${t.activo ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}
                        >
                          {t.activo ? "Activa" : "Inactiva"}
                        </span>
                      </div>
                      <button
                        onClick={() =>
                          handleDeleteTecnico(t.usuario?._id || t.usuario)
                        }
                        className="text-red-400 hover:text-red-600 transition"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-400 text-sm mb-4">
                <User className="h-8 w-8 mx-auto mb-2 opacity-30" />
                Sin tarifas individuales. Todos los técnicos usan la tarifa
                general.
              </div>
            )}

            {/* Agregar nueva tarifa */}
            <div className="border border-dashed border-blue-300 rounded-xl p-4 bg-blue-50">
              <p className="text-xs font-semibold text-blue-700 mb-3">
                + Agregar tarifa individual
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <select
                    value={tecnicoForm.usuario}
                    onChange={(e) =>
                      setTecnicoForm((p) => ({ ...p, usuario: e.target.value }))
                    }
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">Seleccionar técnico...</option>
                    {usuarios
                      .filter(
                        (u) =>
                          !config?.tarifasTecnicos?.find(
                            (t) =>
                              t.usuario?._id === u._id || t.usuario === u._id,
                          ),
                      )
                      .map((u) => (
                        <option key={u._id} value={u._id}>
                          {u.name} — {u.email}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={tecnicoForm.tarifaHora}
                    min="0"
                    onChange={(e) =>
                      setTecnicoForm((p) => ({
                        ...p,
                        tarifaHora: e.target.value,
                      }))
                    }
                    placeholder="Tarifa/hora"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                  <button
                    onClick={handleAddTecnico}
                    disabled={addingTecnico}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </Section>

          {/* SLA por prioridad */}
          <Section
            icon={<Clock className="h-5 w-5 text-orange-600" />}
            title="SLA por prioridad de soporte"
          >
            <p className="text-xs text-gray-500 mb-4">
              Configura los tiempos objetivo de primera respuesta y resolución
              para cada prioridad. Se evalúan automáticamente al cambiar el
              estado del ticket.
            </p>
            <div className="space-y-3">
              {slaConfig.map((sla) => (
                <div
                  key={sla.prioridad}
                  className="flex items-center gap-4 bg-gray-50 rounded-xl p-4 border border-gray-100"
                >
                  <div className="w-24 flex-shrink-0">
                    <span
                      className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORIDAD_COLOR[sla.prioridad]}`}
                    >
                      {sla.prioridad}
                    </span>
                  </div>
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Primera respuesta (horas)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={sla.tiempoRespuestaHoras}
                        onChange={(e) =>
                          handleSlaChange(
                            sla.prioridad,
                            "tiempoRespuestaHoras",
                            e.target.value,
                          )
                        }
                        className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Resolución (horas)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={sla.tiempoResolucionHoras}
                        onChange={(e) =>
                          handleSlaChange(
                            sla.prioridad,
                            "tiempoResolucionHoras",
                            e.target.value,
                          )
                        }
                        className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* TCO */}
          <Section
            icon={<AlertCircle className="h-5 w-5 text-purple-600" />}
            title="Configuración de alertas TCO"
          >
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Umbral de alerta (% sobre costo de compra)
                </label>
                <input
                  type="number"
                  name="tcoAlertaPorcentaje"
                  min="0"
                  max="500"
                  value={form.tcoAlertaPorcentaje}
                  onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Se generará una alerta cuando el costo acumulado de un equipo
                  supere este porcentaje de su costo de compra. Ejemplo: 70 =
                  alertar cuando el costo acumulado supere el 70% del costo
                  original del equipo.
                </p>
              </div>
            </div>
          </Section>
        </div>

        {/* Botón guardar inferior */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleGuardar}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition shadow-lg disabled:opacity-50"
          >
            <Save className="h-5 w-5" />
            {saving ? "Guardando..." : "Guardar toda la configuración"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-2 mb-5">
        {icon}
        <h2 className="text-base font-bold text-gray-800">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function FormField({ label, name, value, onChange, type = "text", help }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
      />
      {help && <p className="text-xs text-gray-400 mt-1">{help}</p>}
    </div>
  );
}
